import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

type PointerPosition = {
  clientX: number;
  clientY: number;
};

@Component({
  selector: 'app-image-zoom',
  templateUrl: './image-zoom.component.html',
  styleUrls: ['./image-zoom.component.css']
})
export class ImageZoomComponent implements OnChanges {

  @ViewChild('chartViewport') chartViewport: ElementRef<HTMLDivElement>;
  @Input() imageSource: any = null;
  @Input() zoomPercent: number = 100;
  @Output() fitZoomCalculated = new EventEmitter<number>();
  @Output() zoomPercentChange = new EventEmitter<number>();

  imageNaturalWidth: number = 0;
  imageNaturalHeight: number = 0;
  isDragging: boolean = false;
  isPinching: boolean = false;

  private activePointers = new Map<number, PointerPosition>();
  private dragPointerId: number | null = null;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private dragStartTranslateX: number = 0;
  private dragStartTranslateY: number = 0;
  private pinchStartDistance: number = 0;
  private pinchAnchorContentX: number = 0;
  private pinchAnchorContentY: number = 0;
  private displayZoomPercent: number = 100;
  private translateX: number = 0;
  private translateY: number = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imageSource'] && changes['imageSource'].currentValue !== changes['imageSource'].previousValue) {
      this.resetInteractionState();
      this.imageNaturalWidth = 0;
      this.imageNaturalHeight = 0;
      this.displayZoomPercent = this.zoomPercent;
      this.translateX = 0;
      this.translateY = 0;
    }

    if (changes['zoomPercent'] && !changes['zoomPercent'].firstChange && this.hasImageDimensions) {
      const nextZoomPercent = Math.max(1, Number(changes['zoomPercent'].currentValue) || 100);
      if (Math.abs(nextZoomPercent - this.displayZoomPercent) > 0.001) {
        this.applyZoomAroundViewportPoint(nextZoomPercent);
      }
    }
  }

  handleImageLoad(event: Event): void {
    const image = event.target as HTMLImageElement;
    this.imageNaturalWidth = image.naturalWidth || image.width;
    this.imageNaturalHeight = image.naturalHeight || image.height;

    const fitZoomPercent = this.getFitZoomPercent();
    this.displayZoomPercent = fitZoomPercent;
    this.centerImage();
    this.fitZoomCalculated.emit(fitZoomPercent);
  }

  handlePointerDown(event: PointerEvent): void {
    if (!this.chartViewport || !this.shouldTrackPointer(event)) {
      return;
    }

    const viewport = this.chartViewport.nativeElement;
    this.activePointers.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY
    });
    viewport.setPointerCapture?.(event.pointerId);

    if (this.activePointers.size === 1) {
      this.beginPointerDrag(event.pointerId, event.clientX, event.clientY);
    } else if (this.activePointers.size === 2) {
      this.beginPointerPinch();
    }

    event.preventDefault();
  }

  handlePointerMove(event: PointerEvent): void {
    if (!this.chartViewport || !this.activePointers.has(event.pointerId)) {
      return;
    }

    this.activePointers.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY
    });

    if (this.isPinching && this.activePointers.size >= 2) {
      this.updatePinchZoom();
    } else if (this.isDragging && this.dragPointerId === event.pointerId) {
      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;
      const nextTransform = this.clampTranslate(
        this.dragStartTranslateX + deltaX,
        this.dragStartTranslateY + deltaY,
        this.displayZoomPercent
      );
      this.translateX = nextTransform.translateX;
      this.translateY = nextTransform.translateY;
    }

    event.preventDefault();
  }

  handlePointerUp(event: PointerEvent): void {
    this.finishPointer(event);
  }

  handlePointerCancel(event: PointerEvent): void {
    this.finishPointer(event);
  }

  get stageTransform(): string {
    return `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.currentScale})`;
  }

  get viewportClass(): string {
    if (this.isPinching) {
      return 'chart-image-viewport is-pinching';
    }

    return this.isDragging ? 'chart-image-viewport is-dragging' : 'chart-image-viewport';
  }

  private get hasImageDimensions(): boolean {
    return this.imageNaturalWidth > 0 && this.imageNaturalHeight > 0;
  }

  private get currentScale(): number {
    return this.displayZoomPercent / 100;
  }

  private beginPointerDrag(pointerId: number, clientX: number, clientY: number): void {
    this.isDragging = true;
    this.isPinching = false;
    this.dragPointerId = pointerId;
    this.dragStartX = clientX;
    this.dragStartY = clientY;
    this.dragStartTranslateX = this.translateX;
    this.dragStartTranslateY = this.translateY;
  }

  private beginPointerPinch(): void {
    if (!this.chartViewport || this.activePointers.size < 2) {
      return;
    }

    const midpoint = this.getPointerMidpoint();
    const currentScale = this.currentScale;
    this.isPinching = true;
    this.isDragging = false;
    this.dragPointerId = null;
    this.pinchStartDistance = Math.max(this.getPointerDistance(), 1);
    this.pinchAnchorContentX = (midpoint.x - this.translateX) / currentScale;
    this.pinchAnchorContentY = (midpoint.y - this.translateY) / currentScale;
  }

  private updatePinchZoom(): void {
    if (!this.chartViewport || this.activePointers.size < 2) {
      return;
    }

    const nextZoomPercent = Math.max(
      1,
      (this.displayZoomPercent * this.getPointerDistance()) / this.pinchStartDistance
    );
    const midpoint = this.getPointerMidpoint();
    this.applyZoomAroundContentPoint(
      nextZoomPercent,
      this.pinchAnchorContentX,
      this.pinchAnchorContentY,
      midpoint.x,
      midpoint.y
    );
    this.pinchStartDistance = Math.max(this.getPointerDistance(), 1);
  }

  private finishPointer(event: PointerEvent): void {
    if (!this.chartViewport || !this.activePointers.has(event.pointerId)) {
      return;
    }

    const viewport = this.chartViewport.nativeElement;
    viewport.releasePointerCapture?.(event.pointerId);
    this.activePointers.delete(event.pointerId);

    if (this.isPinching && this.activePointers.size < 2) {
      this.commitZoomPercent();
    }

    if (this.activePointers.size >= 2) {
      this.beginPointerPinch();
    } else if (this.activePointers.size === 1) {
      const [remainingPointerId, remainingPointer] = Array.from(this.activePointers.entries())[0];
      this.beginPointerDrag(remainingPointerId, remainingPointer.clientX, remainingPointer.clientY);
    } else {
      this.isDragging = false;
      this.isPinching = false;
      this.dragPointerId = null;
    }

    event.preventDefault();
  }

  private commitZoomPercent(): void {
    if (!this.hasImageDimensions) {
      return;
    }

    const viewportCenter = this.getViewportCenter();
    const contentPoint = this.getContentPointAtViewportPoint(viewportCenter.x, viewportCenter.y);
    const roundedZoomPercent = Math.max(1, Math.round(this.displayZoomPercent));
    this.applyZoomAroundContentPoint(
      roundedZoomPercent,
      contentPoint.x,
      contentPoint.y,
      viewportCenter.x,
      viewportCenter.y
    );

    if (roundedZoomPercent !== this.zoomPercent) {
      this.zoomPercentChange.emit(roundedZoomPercent);
    }
  }

  private applyZoomAroundViewportPoint(nextZoomPercent: number): void {
    const viewportCenter = this.getViewportCenter();
    const contentPoint = this.getContentPointAtViewportPoint(viewportCenter.x, viewportCenter.y);
    this.applyZoomAroundContentPoint(
      nextZoomPercent,
      contentPoint.x,
      contentPoint.y,
      viewportCenter.x,
      viewportCenter.y
    );
  }

  private applyZoomAroundContentPoint(
    nextZoomPercent: number,
    contentX: number,
    contentY: number,
    anchorX: number,
    anchorY: number
  ): void {
    if (!this.hasImageDimensions) {
      return;
    }

    const nextScale = nextZoomPercent / 100;
    const unclampedTranslateX = anchorX - (contentX * nextScale);
    const unclampedTranslateY = anchorY - (contentY * nextScale);
    const nextTransform = this.clampTranslate(
      unclampedTranslateX,
      unclampedTranslateY,
      nextZoomPercent
    );

    this.displayZoomPercent = nextZoomPercent;
    this.translateX = nextTransform.translateX;
    this.translateY = nextTransform.translateY;
  }

  private centerImage(): void {
    if (!this.chartViewport || !this.hasImageDimensions) {
      return;
    }

    const viewport = this.chartViewport.nativeElement;
    const scaledWidth = this.imageNaturalWidth * this.currentScale;
    this.translateX = (viewport.clientWidth - scaledWidth) / 2;
    this.translateY = 0;
  }

  private clampTranslate(
    proposedTranslateX: number,
    proposedTranslateY: number,
    zoomPercent: number
  ): { translateX: number; translateY: number } {
    if (!this.chartViewport || !this.hasImageDimensions) {
      return {
        translateX: proposedTranslateX,
        translateY: proposedTranslateY
      };
    }

    const viewport = this.chartViewport.nativeElement;
    const scaledWidth = this.imageNaturalWidth * (zoomPercent / 100);
    const scaledHeight = this.imageNaturalHeight * (zoomPercent / 100);

    const translateX = scaledWidth <= viewport.clientWidth
      ? (viewport.clientWidth - scaledWidth) / 2
      : this.clamp(proposedTranslateX, viewport.clientWidth - scaledWidth, 0);
    const translateY = scaledHeight <= viewport.clientHeight
      ? 0
      : this.clamp(proposedTranslateY, viewport.clientHeight - scaledHeight, 0);

    return {
      translateX,
      translateY
    };
  }

  private getFitZoomPercent(): number {
    if (!this.chartViewport || !this.hasImageDimensions) {
      return 100;
    }

    const viewport = this.chartViewport.nativeElement;
    const widthPercent = (viewport.clientWidth / this.imageNaturalWidth) * 100;
    const heightPercent = (viewport.clientHeight / this.imageNaturalHeight) * 100;
    const fitPercent = Math.min(widthPercent, heightPercent);
    const snappedFitPercent = Math.abs(fitPercent - Math.round(fitPercent)) < 0.001
      ? Math.round(fitPercent)
      : Math.ceil(fitPercent);

    return Math.max(1, snappedFitPercent);
  }

  private getPointerDistance(): number {
    const [firstPointer, secondPointer] = this.getTrackedPointers();
    if (!firstPointer || !secondPointer) {
      return 0;
    }

    return Math.hypot(
      secondPointer.clientX - firstPointer.clientX,
      secondPointer.clientY - firstPointer.clientY
    );
  }

  private getPointerMidpoint(): { x: number; y: number } {
    const [firstPointer, secondPointer] = this.getTrackedPointers();
    const rect = this.chartViewport.nativeElement.getBoundingClientRect();

    return {
      x: (((firstPointer?.clientX ?? 0) + (secondPointer?.clientX ?? 0)) / 2) - rect.left,
      y: (((firstPointer?.clientY ?? 0) + (secondPointer?.clientY ?? 0)) / 2) - rect.top
    };
  }

  private getViewportCenter(): { x: number; y: number } {
    const viewport = this.chartViewport.nativeElement;
    return {
      x: viewport.clientWidth / 2,
      y: viewport.clientHeight / 2
    };
  }

  private getContentPointAtViewportPoint(viewportX: number, viewportY: number): { x: number; y: number } {
    const scale = this.currentScale;
    return {
      x: (viewportX - this.translateX) / scale,
      y: (viewportY - this.translateY) / scale
    };
  }

  private getTrackedPointers(): PointerPosition[] {
    return Array.from(this.activePointers.values()).slice(0, 2);
  }

  private shouldTrackPointer(event: PointerEvent): boolean {
    if (this.activePointers.has(event.pointerId)) {
      return true;
    }

    if (this.activePointers.size >= 2) {
      return false;
    }

    if (event.pointerType === 'mouse' && event.button !== 0) {
      return false;
    }

    return true;
  }

  private resetInteractionState(): void {
    this.activePointers.clear();
    this.isDragging = false;
    this.isPinching = false;
    this.dragPointerId = null;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.dragStartTranslateX = 0;
    this.dragStartTranslateY = 0;
    this.pinchStartDistance = 0;
    this.pinchAnchorContentX = 0;
    this.pinchAnchorContentY = 0;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
